import type { CustomerConfig } from "@raventic/sdk";
import { defineCarousel, defineDropdown, defineResults, ga4Event, onDataLayer } from "@raventic/sdk";
import { cookieConsent } from "@raventic/sdk/consent";
import { cartConfig } from "./components/cart";
import { param } from "@raventic/sdk/helpers";
import { RcobchodCard } from "./components/RcobchodCard";
import { availabilityTone } from "./components/view";
import { SEARCH_INPUT_SELECTORS, SEARCH_PATH, SEARCH_QUERY_PARAMETER, viewItemProductId } from "./page";
import { lockPageScroll, unlockPageScroll } from "./search";

// rcobchod.cz: search dropdown, search results and a visual-similarity carousel on the product
// detail. A new integration — it replaces Luigi's Box (search) on the site, there were no old
// Raventic snippets to port. Decisions and data gaps: CARD.md / ACCOUNT.md.
//
// The config reference (activation model, define* helpers, styling tiers) is in
// templates/customer/config.ts.

const API_KEY = "eucb88a9d434b2083da52683c505056";

// Shared by every widget. The API key goes on the search configs only: rce takes it from
// `rceConfig` (its widgetConfig type omits `apiKey`).
const localization = {
  locale: "cs-CZ",
  currency: "CZK",
  analyticsEventsTarget: "dataLayer",
  priceDecimals: 0,
} as const;

// Our host class on the light-DOM card widgets (results + carousel): light.scss scopes every card
// rule under it.
const CARD_HOST_CLASS = "raventic-rc";

// The light-DOM listing card (RcobchodCard) and its cart button: the SDK's own AJAX cart posts the
// product, then refreshes the header totals and opens the shop's modal (components/cart.ts, CARD.md
// "Cart button"). `button-type` is "cart" on every product today.
const cardWidget = {
  card: RcobchodCard,
  cartConfig,
  buttonTypeParameterName: "button-type",
  buttonLabelParameterName: "button-text",
  defaultButtonLabel: "Do košíku",
  // The card's image slot is 150px high and at most ~255px wide (produkt.css `.box .thumbnail`); a
  // fitted (uncropped) image of that shape at 2x never gets cut off at the card's edges.
  thumbnailDimensions: { width: 460, height: 300 },
  thumbnailMode: "ft",
} as const;

export const config: CustomerConfig = {
  // Consent follows the shop's CookieConsent banner (orestbida v2.8.0), "statistical" category — the
  // shop's own name, not the library's usual "analytics" (probe 2026-10-18: accept all writes
  // `cc_cookie` = {"level":["necessary","statistical","marketing","other"],…}, reject all
  // {"level":["necessary"],…}; v2 has no global to read, the cookie is the only source). Without
  // it the events are still sent, but anonymously (one fixed id, nothing stored in the browser).
  // `window.RaventicConsent`, if set, overrides this.
  analytics: {
    apiKey: API_KEY,
    consent: cookieConsent({ category: "statistical" }),
  },

  search: {
    // The SDK's DEFAULT card, ported from the dropdown config rcobchod already has (the Raventic
    // snippet predating this bundle; CARD.md "Dropdown card"): texts, labels, detail button, count and
    // breakpoints as there, its `customStyles` in styles/shadow-product.scss.
    dropdown: defineDropdown({
      activateOn: SEARCH_INPUT_SELECTORS,
      // The page stays put while the dropdown is open (search.ts: otherwise it scrolls away).
      callbacks: { onOpen: lockPageScroll, onClose: unlockPageScroll },
      config: {
        apiKey: API_KEY,
        ...localization,
        zIndex: 9999,
        submitUrl: `https://www.rcobchod.cz${SEARCH_PATH}`,
        queryParameterName: SEARCH_QUERY_PARAMETER,
        inputPlaceholder: "Napište, co hledáte",
        welcomeMessage: "Najděte přesně to, co hledáte!",
        searchingForMessage: "Hledáme frázi",
        wouldYouLikeToSearchForMessage: "chcete raději hledat",
        // The initial state (empty query): the SDK's recommended products.
        recommendedProductsTitle: "Oblíbené produkty",
        foundProductsTitle: "Produkty",
        foundProductsTitleShort: "Produkty",
        noResultsFoundMessage: "Nic jsme nenašli, zkuste to znovu",
        showAllMessage: "ZOBRAZIT VŠECHNY VÝSLEDKY",
        // Additional content (added 2026-09-25): categories, then articles, in the SDK's default
        // content list. Type ids as indexed; the search API served neither yet when this was added
        // (CARD.md "Dropdown card").
        content: [
          { type: "categories", title: "Kategorie", limit: 6, initialItems: [] },
          // `articles_test`: the articles' test index, on the account's request (2026-09-25); switch to
          // `articles` once the production index is served.
          { type: "articles_test", title: "Články", limit: 6, initialItems: [] },
        ],
        // Every card's button goes to the detail (no cart in the dropdown); `button-type` is not read.
        defaultButtonLabel: "Zobrazit detail",
        defaultButtonToDetail: true,
        // The `label` params as the card's labels, colored in shadow-product.scss. No built-in in-stock
        // label: it keys on `_in-stock`, which is unreliable here (CARD.md "Availability").
        labelsPosition: "top-left",
        customLabelsPosition: "top-left",
        customLabelParameters: ["label"],
        // Availability: `stock_text` as printed, under the name, green for any "Skladem…" and dark
        // otherwise — the listing cards' rule (view.ts availabilityTone; shadow-product.scss).
        individualParameters: [
          {
            parameterName: "stock_text",
            title: "Dostupnost",
            classes: (product) => `raventic-rc-stock--${availabilityTone(param(product, "stock_text") ?? "")}`,
          },
        ],
        resultsCount: 10,
        breakpoints: [640, 768, 1024, 1280, 1536],
        submitToDetailOnSingleResult: false,
      },
      // Luigi's Box autocomplete, in case its script is still on the page (hidden from our
      // dropdown's first open on).
      hide: ".luigi-ac",
    }),

    results: defineResults({
      activateWhen: (ctx) => ctx.url.pathname.startsWith(SEARCH_PATH),
      // The search page's `#lbx-wrapper` container (Luigi's Box rendered its results into it): the
      // SDK empties it (the native h1 + loader) and mounts; the container gives the page width.
      target: "#lbx-wrapper > .container",
      config: {
        apiKey: API_KEY,
        ...localization,
        ...cardWidget,
        containerClassName: CARD_HOST_CLASS,
        queryParameterName: SEARCH_QUERY_PARAMETER,
        // 48 = the category listing's page size; divides into its 3 / 4 columns.
        pageSize: 48,
        desktopMode: "left",
        collapsibleFacets: true,
        showManufacturers: true,
        title: "Výsledky hledání",
        noQueryTitle: "Produkty",
        productsTitle: "Produkty",
        noResultsFoundMessage: "Nic jsme nenašli, zkuste jiný výraz",
        searchingForMessage: "Hledáme výraz",
        wouldYouLikeToSearchForMessage: "chcete raději hledat",
        categoriesTitle: "Kategorie",
        manufacturersTitle: "Výrobce",
        priceTitle: "Cena",
        filtersTitle: "Filtry",
        filterButtonTitle: "Filtrovat",
        clearAllFiltersTitle: "Zrušit filtry",
        sortByTitle: "Řadit podle",
      },
    }),
  },

  rce: {
    carousels: [
      // Product detail: visual similarity, identified by the GA4 `view_item` push (page.ts). Once,
      // replayed (the push precedes widget.js). Right under the product info, above the site's
      // "Související produkty" slider: drawn in that section, before its container (both stay). The
      // section is on every product page; the native "Podobné produkty" further down stays too.
      defineCarousel({
        type: "product",
        activateWhen: onDataLayer(ga4Event("view_item")),
        rceConfig: { apiKey: API_KEY, service: "visual-similarity", variant: "default" },
        widgetConfig: {
          ...localization,
          ...cardWidget,
          resultsCount: 15,
          size: "large",
          // Inside the native section (its white background and spacing): the site paints backgrounds
          // per full-width <section> over a grey body, and `.container` caps at 1436px, so a block of
          // our own between two sections would sit on grey on wide screens.
          targetContainerSelector: "section.section-produkty-minibox > .container",
          targetContainerMode: "before",
          // Named container: a stable hook for our CSS and the probe presets. The site's `container`
          // gives the page width and side padding.
          defaultContainerId: "raventic-rc-similarity",
          containerClassName: `${CARD_HOST_CLASS} raventic-rc-carousel container`,
          // As the account asked (2026-09-25), although the native block of the same name stays further
          // down the page (ACCOUNT.md "Open decisions").
          title: "Podobné produkty",
        },
        productId: (ctx) => viewItemProductId(ctx.trigger),
      }),
    ],
  },
};
